import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../AuthProvider'
import { api } from '../api'

/**
 * Magic-link landing: /app/verify-email?token=…
 * Verifies email, stores session, enters client portal.
 * Also accepts legacy ?verify= for older emails.
 */
export function ClientVerifyEmailPage() {
  const { isAuthenticated, acceptSession } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')
  const [status, setStatus] = useState<'working' | 'done' | 'error'>('working')
  const started = useRef(false)

  const token = (searchParams.get('token') || searchParams.get('verify') || '').trim()

  useEffect(() => {
    if (started.current) return
    if (!token) {
      setStatus('error')
      setError('This verification link is missing a token. Request a new email from registration.')
      return
    }
    started.current = true
    void (async () => {
      try {
        const res = await api.verifyEmail(token)
        if (!res.success && !res.emailVerified) {
          setStatus('error')
          setError('Verification failed')
          return
        }
        if (res.accessToken) {
          acceptSession(res.accessToken, res.client || null)
          setStatus('done')
          navigate('/app', { replace: true })
          return
        }
        setStatus('done')
        navigate('/app/login', { replace: true })
      } catch (err) {
        setStatus('error')
        setError(err instanceof Error ? err.message : 'Verification failed')
      }
    })()
  }, [token, acceptSession, navigate])

  if (isAuthenticated && status !== 'error') {
    return <Navigate to="/app" replace />
  }

  return (
    <div className="cp-auth cp-auth--verify">
      <div className="cp-auth__card">
        <p className="cp-eyebrow">Client Panel</p>
        <h1>
          {status === 'error'
            ? 'Verification failed'
            : status === 'done'
              ? 'Email verified'
              : 'Verifying email…'}
        </h1>
        {status === 'working' ? (
          <p className="cp-muted">Activating your Perfect Predict account. One moment.</p>
        ) : null}
        {status === 'done' ? (
          <p className="cp-ok">Opening your Client Portal…</p>
        ) : null}
        {error ? (
          <p className="cp-error" role="alert">
            {error}
          </p>
        ) : null}
        {status === 'error' ? (
          <p className="cp-muted">
            <Link to="/app/login">Sign in</Link>
            {' · '}
            <Link to="/app/register">Create account</Link>
          </p>
        ) : null}
      </div>
    </div>
  )
}
